import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { cn } from '@/lib/utils';
import {
	DIALOG_CANCEL_CLASS,
	DIALOG_CONFIRM_CLASS,
	DIALOG_CONTENT_CLASS,
	DIALOG_DANGER_CLASS,
	DIALOG_DESCRIPTION_CLASS,
	DIALOG_FOOTER_CLASS,
	DIALOG_HEADER_CLASS,
	DIALOG_TITLE_CLASS
} from './dialog-styles';

interface ConfirmToggleDialogProps {
	/** null = closed; true = confirm turning ON; false = confirm turning OFF. */
	pendingValue: boolean | null;
	icon: LucideIcon;
	title: string;
	description: ReactNode;
	confirmLabel: string;
	isPending?: boolean;
	onConfirm: () => void;
	onCancel: () => void;
}

/** Enable/disable confirmation, styled like the Intent-Based Filtering one. Red when turning off. */
export function ConfirmToggleDialog({
	pendingValue,
	icon: Icon,
	title,
	description,
	confirmLabel,
	isPending = false,
	onConfirm,
	onCancel
}: ConfirmToggleDialogProps) {
	const isDisable = pendingValue === false;
	return (
		<AlertDialog
			open={pendingValue !== null}
			onOpenChange={(open) => {
				if (!open) onCancel();
			}}
		>
			<AlertDialogContent className={DIALOG_CONTENT_CLASS}>
				<AlertDialogHeader className={DIALOG_HEADER_CLASS}>
					<div className='flex flex-col gap-1.5'>
						<Icon className={cn('h-7 w-7', isDisable ? 'text-red-500' : 'text-indigo-500')} />
						<AlertDialogTitle className={DIALOG_TITLE_CLASS}>{title}</AlertDialogTitle>
					</div>
					<AlertDialogDescription asChild>
						<div className={cn(DIALOG_DESCRIPTION_CLASS, 'flex flex-col gap-2')}>{description}</div>
					</AlertDialogDescription>
				</AlertDialogHeader>
				<AlertDialogFooter className={DIALOG_FOOTER_CLASS}>
					<AlertDialogCancel disabled={isPending} className={DIALOG_CANCEL_CLASS}>
						Cancel
					</AlertDialogCancel>
					<AlertDialogAction
						onClick={onConfirm}
						disabled={isPending}
						className={isDisable ? DIALOG_DANGER_CLASS : DIALOG_CONFIRM_CLASS}
					>
						{isPending ? (
							<span className='flex items-center gap-2'>
								<span className='h-3 w-3 rounded-full border-2 border-current border-t-transparent animate-spin' />
								Saving…
							</span>
						) : (
							confirmLabel
						)}
					</AlertDialogAction>
				</AlertDialogFooter>
			</AlertDialogContent>
		</AlertDialog>
	);
}
